/**
 * AnalyticsHome (Reportes Guardados) — the well's analytics home. A tabbed page
 * over five SERVER-computed roll-ups from Oxbo-P: ROP by section, NPT by
 * section, time-vs-depth, job-by-depth, and end-of-well. Every KPI is served
 * (Oxbo-Z never recomputes one); charts are hand-rolled, self-contained SVG in
 * the style of SurveyPlots (no external CDN, no chart lib).
 *
 * Mounted by the lead on the "Reportes guardados" (/saved) route as
 * <AnalyticsHome wellUwi={…} />. It also falls back to the current well from
 * context when no prop is supplied, so it can stand alone.
 */
import { useState } from "react";
import { useLocale } from "@hooks/useLocale";
import { useCurrentWell } from "@hooks/useCurrentWell";
import { QueryState } from "@components/ui/QueryState";
import { StatBand, type Stat } from "@components/ui/StatBand";
import { fmtDepth, fmtHours, fmtMoney, fmtNum, fmtPct, intlTag } from "@lib/format";
import type { Locale } from "@i18n/locales";
import type { MessageKey } from "@i18n/messages";
import {
  useGetRopBySectionQuery,
  useGetNptBySectionQuery,
  useGetTimeVsDepthQuery,
  useGetEndOfWellQuery,
  useGetFullJobByDepthQuery,
  type TimeDepthPoint,
} from "./analyticsApi";

type TabId = "rop" | "npt" | "timeDepth" | "byDepth" | "endOfWell";

const TABS: { id: TabId; key: MessageKey }[] = [
  { id: "rop", key: "an.tab.rop" },
  { id: "npt", key: "an.tab.npt" },
  { id: "timeDepth", key: "an.tab.timeDepth" },
  { id: "byDepth", key: "an.tab.byDepth" },
  { id: "endOfWell", key: "an.tab.endOfWell" },
];

/** Format an amount, honouring "MIXED" (no false currency symbol). */
function fmtAmount(
  amount: number | null | undefined,
  currency: string | undefined,
  locale: Locale,
  compact = false,
): string {
  if (amount == null || !Number.isFinite(amount)) return "—";
  if (!currency || currency === "MIXED") {
    return fmtNum(amount, locale, { maximumFractionDigits: compact ? 1 : 0 });
  }
  return fmtMoney(amount, locale, { currency, compact });
}

/** A date string ("YYYY-MM-DD" or ISO) in the tenant's locale. */
function fmtDate(iso: string | null | undefined, locale: Locale): string {
  if (!iso) return "—";
  const d = new Date(iso.length <= 10 ? `${iso}T00:00:00` : iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(intlTag(locale), {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

// ── Shared SVG primitives ───────────────────────────────────────────────────
const CW = 640; // intrinsic chart width
const CH = 300; // intrinsic chart height
const CPAD = { top: 16, right: 18, bottom: 44, left: 56 };

/** Map a data range onto a pixel range, guarding a zero-width domain. */
function scaler(dMin: number, dMax: number, pMin: number, pMax: number) {
  const span = dMax - dMin;
  if (!Number.isFinite(span) || Math.abs(span) < 1e-9) {
    const mid = (pMin + pMax) / 2;
    return () => mid;
  }
  return (v: number) => pMin + ((v - dMin) / span) * (pMax - pMin);
}

function ChartFrame({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="card">
      <h3>{title}</h3>
      <svg
        viewBox={`0 0 ${CW} ${CH}`}
        role="img"
        aria-label={title}
        style={{
          width: "100%",
          height: "auto",
          background: "var(--surface)",
          border: "1px solid var(--line)",
          borderRadius: 8,
          display: "block",
        }}
      >
        {children}
      </svg>
    </div>
  );
}

function AxisText(props: {
  x: number;
  y: number;
  children: React.ReactNode;
  anchor?: "start" | "middle" | "end";
  rotate?: number;
  dim?: boolean;
}) {
  const { x, y, children, anchor = "middle", rotate, dim = true } = props;
  return (
    <text
      x={x}
      y={y}
      fontSize={10}
      fill={dim ? "var(--dim)" : "var(--ink)"}
      textAnchor={anchor}
      transform={rotate ? `rotate(${rotate} ${x} ${y})` : undefined}
    >
      {children}
    </text>
  );
}

/**
 * A vertical bar chart over labelled categories. `values` may contain nulls
 * (rendered as a gap). Bars scale to the max finite value.
 */
function BarChart({
  categories,
  values,
  valueLabel,
  color = "var(--brand)",
  fmtValue,
}: {
  categories: string[];
  values: (number | null)[];
  valueLabel: string;
  color?: string;
  fmtValue: (v: number) => string;
}) {
  const finite = values.filter((v): v is number => v != null && Number.isFinite(v));
  const maxV = finite.length ? Math.max(...finite, 0) : 0;
  const x0 = CPAD.left;
  const x1 = CW - CPAD.right;
  const y0 = CH - CPAD.bottom;
  const y1 = CPAD.top;
  const sy = scaler(0, maxV || 1, y0, y1);
  const n = Math.max(categories.length, 1);
  const slot = (x1 - x0) / n;
  const bw = Math.max(4, slot * 0.6);
  return (
    <>
      {/* y axis + zero baseline */}
      <line x1={x0} y1={y1} x2={x0} y2={y0} stroke="var(--line)" />
      <line x1={x0} y1={y0} x2={x1} y2={y0} stroke="var(--line)" />
      <AxisText x={14} y={(y0 + y1) / 2} rotate={-90}>
        {valueLabel}
      </AxisText>
      {categories.map((cat, i) => {
        const v = values[i];
        const cx = x0 + slot * i + slot / 2;
        const barX = cx - bw / 2;
        const barY = v != null && Number.isFinite(v) ? sy(v) : y0;
        const h = y0 - barY;
        return (
          <g key={`${cat}-${i}`}>
            {v != null && Number.isFinite(v) ? (
              <>
                <rect x={barX} y={barY} width={bw} height={Math.max(0, h)} fill={color} rx={2} />
                <text x={cx} y={barY - 4} fontSize={9} fill="var(--dim)" textAnchor="middle">
                  {fmtValue(v)}
                </text>
              </>
            ) : null}
            <AxisText x={cx} y={y0 + 14}>
              {cat}
            </AxisText>
          </g>
        );
      })}
    </>
  );
}

// ── Section 1 — ROP by section ──────────────────────────────────────────────
function RopSection({ wellUwi }: { wellUwi: string }) {
  const { t, locale } = useLocale();
  const { data, isLoading, isError, refetch } = useGetRopBySectionQuery(wellUwi, {
    skip: !wellUwi,
  });
  const sections = data?.sections ?? [];
  return (
    <QueryState
      isLoading={isLoading}
      isError={isError}
      isEmpty={!!data && sections.length === 0}
      emptyMessage={t("an.noData")}
      onRetry={refetch}
    >
      <ChartFrame title={t("an.rop.chartTitle")}>
        <BarChart
          categories={sections.map((s) => s.section)}
          values={sections.map((s) => s.rop)}
          valueLabel={t("an.rop.axis.rop")}
          fmtValue={(v) => fmtNum(v, locale, { maximumFractionDigits: 1 })}
        />
      </ChartFrame>
      <div className="card" style={{ marginTop: 14 }}>
        <h3>{t("an.rop.title")}</h3>
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>{t("an.rop.col.section")}</th>
                <th className="num">{t("an.rop.col.footage")}</th>
                <th className="num">{t("an.rop.col.hours")}</th>
                <th className="num">{t("an.rop.col.rop")}</th>
              </tr>
            </thead>
            <tbody>
              {sections.map((s) => (
                <tr key={s.section} data-owner="p">
                  <td>{s.section}</td>
                  <td className="num tab-nums">{fmtDepth(s.footage, locale)}</td>
                  <td className="num tab-nums">{fmtHours(s.hours, locale)}</td>
                  <td className="num tab-nums">
                    {fmtNum(s.rop, locale, { maximumFractionDigits: 1 })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </QueryState>
  );
}

// ── Section 2 — NPT by section ──────────────────────────────────────────────
function NptSection({ wellUwi }: { wellUwi: string }) {
  const { t, locale } = useLocale();
  const { data, isLoading, isError, refetch } = useGetNptBySectionQuery(wellUwi, {
    skip: !wellUwi,
  });
  const sections = data?.sections ?? [];
  const stats: Stat[] = data
    ? [
        { label: t("an.npt.kpi.totalNptHours"), value: fmtHours(data.total_npt_hours, locale), tone: "warn" },
        { label: t("an.npt.kpi.totalHours"), value: fmtHours(data.total_hours, locale) },
        {
          label: t("an.npt.kpi.nptPct"),
          value: fmtPct(data.npt_pct, locale, 1),
          tone: data.npt_pct != null && data.npt_pct > 10 ? "crit" : "warn",
        },
      ]
    : [];
  return (
    <QueryState
      isLoading={isLoading}
      isError={isError}
      isEmpty={!!data && sections.length === 0}
      emptyMessage={t("an.noData")}
      onRetry={refetch}
    >
      {stats.length ? <StatBand stats={stats} /> : null}
      <div style={{ marginTop: 14 }}>
        <ChartFrame title={t("an.npt.chartTitle")}>
          <BarChart
            categories={sections.map((s) => s.section)}
            values={sections.map((s) => s.npt_pct)}
            valueLabel={t("an.npt.axis.pct")}
            color="var(--warn, #d29922)"
            fmtValue={(v) => fmtPct(v, locale, 1)}
          />
        </ChartFrame>
      </div>
      <div className="card" style={{ marginTop: 14 }}>
        <h3>{t("an.npt.title")}</h3>
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>{t("an.npt.col.section")}</th>
                <th className="num">{t("an.npt.col.nptHours")}</th>
                <th className="num">{t("an.npt.col.totalHours")}</th>
                <th className="num">{t("an.npt.col.nptPct")}</th>
              </tr>
            </thead>
            <tbody>
              {sections.map((s) => (
                <tr key={s.section} data-owner="p">
                  <td>{s.section}</td>
                  <td className="num tab-nums">{fmtHours(s.npt_hours, locale)}</td>
                  <td className="num tab-nums">{fmtHours(s.total_hours, locale)}</td>
                  <td className="num tab-nums">{fmtPct(s.npt_pct, locale, 1)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </QueryState>
  );
}

// ── Section 3 — Time vs depth ───────────────────────────────────────────────
/** A named series for the classic time-vs-depth curve (days x, MD y down). */
interface TdSeries {
  key: string;
  label: string;
  color: string;
  dashed?: boolean;
  points: TimeDepthPoint[];
}

function TimeDepthChart({ series, t, locale }: { series: TdSeries[]; t: (k: MessageKey) => string; locale: Locale }) {
  const all = series.flatMap((s) => s.points).filter((p) => p.md != null && Number.isFinite(p.md));
  const days = all.map((p) => p.day);
  const mds = all.map((p) => p.md as number);
  const maxDay = days.length ? Math.max(...days, 0) : 1;
  const maxMd = mds.length ? Math.max(...mds, 0) : 1;
  const x0 = CPAD.left;
  const x1 = CW - CPAD.right;
  const y0 = CPAD.top; // MD 0 at top
  const y1 = CH - CPAD.bottom; // max MD at bottom (depth increases downward)
  const sx = scaler(0, maxDay || 1, x0, x1);
  const sy = scaler(0, maxMd || 1, y0, y1);
  return (
    <ChartFrame title={t("an.td.chartTitle")}>
      {/* axes: x along the top (days), y down the left (MD) */}
      <line x1={x0} y1={y0} x2={x1} y2={y0} stroke="var(--line)" />
      <line x1={x0} y1={y0} x2={x0} y2={y1} stroke="var(--line)" />
      <AxisText x={(x0 + x1) / 2} y={y0 - 4}>
        {t("an.td.axis.days")} (0–{fmtNum(maxDay, locale)})
      </AxisText>
      <AxisText x={16} y={(y0 + y1) / 2} rotate={-90}>
        {t("an.td.axis.md")} ({fmtDepth(maxMd, locale)})
      </AxisText>
      {series.map((s) => {
        const pts = s.points
          .filter((p) => p.md != null && Number.isFinite(p.md))
          .map((p) => ({ x: sx(p.day), y: sy(p.md as number) }));
        if (pts.length === 0) return null;
        const d = pts
          .map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(2)} ${p.y.toFixed(2)}`)
          .join(" ");
        return (
          <g key={s.key}>
            <path
              d={d}
              fill="none"
              stroke={s.color}
              strokeWidth={1.75}
              strokeDasharray={s.dashed ? "5 4" : undefined}
              strokeLinejoin="round"
              strokeLinecap="round"
            />
            {pts.map((p, i) => (
              <circle key={`${s.key}-${i}`} cx={p.x} cy={p.y} r={2} fill={s.color} />
            ))}
          </g>
        );
      })}
    </ChartFrame>
  );
}

function TimeDepthSection({ wellUwi }: { wellUwi: string }) {
  const { t, locale } = useLocale();
  const { data, isLoading, isError, refetch } = useGetTimeVsDepthQuery(wellUwi, {
    skip: !wellUwi,
  });
  const actual = data?.actual ?? [];
  // Series structure is ready for plan/p10/p90 the moment Oxbo-P serves them.
  const series: TdSeries[] = [];
  if (actual.length) {
    series.push({
      key: "actual",
      label: t("an.td.legend.actual"),
      color: "var(--brand)",
      points: actual,
    });
  }
  if (data?.planned?.length) {
    series.push({ key: "planned", label: "Plan", color: "var(--dim)", dashed: true, points: data.planned });
  }
  if (data?.p10?.length) {
    series.push({ key: "p10", label: "P10", color: "var(--good, #3fb950)", dashed: true, points: data.p10 });
  }
  if (data?.p90?.length) {
    series.push({ key: "p90", label: "P90", color: "var(--crit, #f85149)", dashed: true, points: data.p90 });
  }
  const hasPlan = data?.plan_available === true;
  return (
    <QueryState
      isLoading={isLoading}
      isError={isError}
      isEmpty={!!data && actual.length === 0}
      emptyMessage={t("an.noData")}
      onRetry={refetch}
    >
      <TimeDepthChart series={series} t={t} locale={locale} />
      {!hasPlan ? (
        <div className="card" style={{ marginTop: 14 }}>
          <p className="subtitle" style={{ color: "var(--dim)", margin: 0 }}>
            {data?.note ?? t("an.td.noPlan")}
          </p>
        </div>
      ) : null}
    </QueryState>
  );
}

// ── Section 4 — Job by depth ────────────────────────────────────────────────
const BUCKET_SIZES = [250, 500, 1000];

function ByDepthSection({ wellUwi }: { wellUwi: string }) {
  const { t, locale } = useLocale();
  const [bucketSize, setBucketSize] = useState(500);
  const { data, isLoading, isError, refetch } = useGetFullJobByDepthQuery(
    { wellUwi, bucketSize },
    { skip: !wellUwi },
  );
  const buckets = data?.buckets ?? [];
  const currency = data?.currency;
  const stats: Stat[] = data
    ? [
        { label: t("an.byDepth.kpi.hours"), value: fmtHours(data.total_hours, locale) },
        { label: t("an.byDepth.kpi.nptHours"), value: fmtHours(data.total_npt_hours, locale), tone: "warn" },
        { label: t("an.byDepth.kpi.cost"), value: fmtAmount(data.total_cost, currency, locale, true) },
      ]
    : [];
  return (
    <QueryState
      isLoading={isLoading}
      isError={isError}
      isEmpty={!!data && buckets.length === 0}
      emptyMessage={t("an.noData")}
      onRetry={refetch}
    >
      <div
        style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12, flexWrap: "wrap" }}
      >
        <span className="subtitle" style={{ color: "var(--dim)" }}>
          {t("an.byDepth.bucketSize")}
        </span>
        <div className="seg" role="group" aria-label={t("an.byDepth.bucketSize")}>
          {BUCKET_SIZES.map((bs) => (
            <button
              key={bs}
              type="button"
              className={bucketSize === bs ? "active" : ""}
              aria-pressed={bucketSize === bs}
              onClick={() => setBucketSize(bs)}
            >
              {fmtNum(bs, locale)}
            </button>
          ))}
        </div>
      </div>
      {stats.length ? <StatBand stats={stats} /> : null}
      <div style={{ marginTop: 14 }}>
        <ChartFrame title={t("an.byDepth.title")}>
          <BarChart
            categories={buckets.map((bk) => fmtNum(bk.depth_from, locale))}
            values={buckets.map((bk) => bk.hours)}
            valueLabel={t("an.byDepth.col.hours")}
            fmtValue={(v) => fmtNum(v, locale, { maximumFractionDigits: 0 })}
          />
        </ChartFrame>
      </div>
      <div className="card" style={{ marginTop: 14 }}>
        <h3>{t("an.byDepth.title")}</h3>
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>{t("an.byDepth.col.interval")}</th>
                <th className="num">{t("an.byDepth.col.hours")}</th>
                <th className="num">{t("an.byDepth.col.nptHours")}</th>
                <th className="num">{t("an.byDepth.col.footage")}</th>
                <th className="num">{t("an.byDepth.col.cost")}</th>
                <th className="num">{t("an.byDepth.col.activities")}</th>
              </tr>
            </thead>
            <tbody>
              {buckets.map((bk) => (
                <tr key={`${bk.depth_from}-${bk.depth_to}`} data-owner="p">
                  <td className="tab-nums">
                    {fmtDepth(bk.depth_from, locale)} – {fmtDepth(bk.depth_to, locale)}
                  </td>
                  <td className="num tab-nums">{fmtHours(bk.hours, locale)}</td>
                  <td className="num tab-nums">{fmtHours(bk.npt_hours, locale)}</td>
                  <td className="num tab-nums">{fmtDepth(bk.footage, locale)}</td>
                  <td className="num tab-nums">{fmtAmount(bk.cost, currency, locale)}</td>
                  <td className="num tab-nums">{fmtNum(bk.activity_count, locale)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </QueryState>
  );
}

// ── Section 5 — End of well ─────────────────────────────────────────────────
function EndOfWellSection({ wellUwi }: { wellUwi: string }) {
  const { t, locale } = useLocale();
  const { data, isLoading, isError, refetch } = useGetEndOfWellQuery(wellUwi, {
    skip: !wellUwi,
  });
  const currency = data?.currency;
  const stats: Stat[] = data
    ? [
        { label: t("an.eow.kpi.totalDays"), value: fmtNum(data.total_days, locale, { maximumFractionDigits: 1 }) },
        { label: t("an.eow.kpi.operatingDays"), value: fmtNum(data.operating_days, locale, { maximumFractionDigits: 1 }) },
        { label: t("an.eow.kpi.footage"), value: fmtDepth(data.total_footage, locale) },
        { label: t("an.eow.kpi.mdFinal"), value: fmtDepth(data.md_final, locale) },
        { label: t("an.eow.kpi.tvdFinal"), value: fmtDepth(data.tvd_final, locale) },
        { label: t("an.eow.kpi.totalCost"), value: fmtAmount(data.total_cost, currency, locale, true) },
        { label: t("an.eow.kpi.ropOverall"), value: fmtNum(data.rop_overall, locale, { maximumFractionDigits: 1 }) },
        {
          label: t("an.eow.kpi.nptPct"),
          value: fmtPct(data.npt_pct, locale, 1),
          tone: data.npt_pct != null && data.npt_pct > 10 ? "crit" : "warn",
        },
      ]
    : [];
  const sections = data?.sections ?? [];
  return (
    <QueryState
      isLoading={isLoading}
      isError={isError}
      isEmpty={!data}
      emptyMessage={t("an.noData")}
      onRetry={refetch}
    >
      {stats.length ? <StatBand stats={stats} /> : null}
      {data ? (
        <div className="card" style={{ marginTop: 14 }}>
          <div className="chip-list" style={{ display: "flex", gap: 18, flexWrap: "wrap" }}>
            <span className="subtitle">
              {t("an.eow.spud")}: <strong>{fmtDate(data.spud_date, locale)}</strong>
            </span>
            <span className="subtitle">
              {t("an.eow.lastReport")}: <strong>{fmtDate(data.last_report_date, locale)}</strong>
            </span>
            <span className="subtitle">
              {t("an.eow.reportCount")}: <strong>{fmtNum(data.report_count, locale)}</strong>
            </span>
          </div>
        </div>
      ) : null}
      <div className="card" style={{ marginTop: 14 }}>
        <h3>{t("an.eow.sections")}</h3>
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>{t("an.eow.col.section")}</th>
                <th className="num">{t("an.eow.col.footage")}</th>
                <th className="num">{t("an.eow.col.hours")}</th>
                <th className="num">{t("an.eow.col.nptHours")}</th>
                <th className="num">{t("an.eow.col.nptPct")}</th>
                <th className="num">{t("an.eow.col.rop")}</th>
              </tr>
            </thead>
            <tbody>
              {sections.map((s) => (
                <tr key={s.section} data-owner="p">
                  <td>{s.section}</td>
                  <td className="num tab-nums">{fmtDepth(s.footage, locale)}</td>
                  <td className="num tab-nums">{fmtHours(s.hours, locale)}</td>
                  <td className="num tab-nums">{fmtHours(s.npt_hours, locale)}</td>
                  <td className="num tab-nums">{fmtPct(s.npt_pct, locale, 1)}</td>
                  <td className="num tab-nums">
                    {fmtNum(s.rop, locale, { maximumFractionDigits: 1 })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </QueryState>
  );
}

/**
 * AnalyticsHome — the tabbed analytics page for a well. Accepts `wellUwi` as a
 * prop (so the lead can mount it on /saved); falls back to the current well
 * from context when the prop is omitted.
 */
export function AnalyticsHome({ wellUwi }: { wellUwi?: string }) {
  const { t } = useLocale();
  const { well } = useCurrentWell();
  const uwi = wellUwi ?? well?.uwi ?? "";
  const [tab, setTab] = useState<TabId>("rop");

  return (
    <div>
      <div className="page-head">
        <h1>{t("an.title")}</h1>
        <p className="subtitle">
          {well ? `${well.commonName} · ${well.field}` : t("an.subtitle")}
        </p>
      </div>

      <div className="tabbar" role="tablist" aria-label={t("an.title")}>
        {TABS.map((tb) => (
          <button
            key={tb.id}
            type="button"
            role="tab"
            aria-selected={tab === tb.id}
            className={tab === tb.id ? "active" : ""}
            onClick={() => setTab(tb.id)}
          >
            {t(tb.key)}
          </button>
        ))}
      </div>

      <div className="tab-panel" role="tabpanel" style={{ marginTop: 14 }}>
        {!uwi ? (
          <div className="empty-state">{t("an.noData")}</div>
        ) : tab === "rop" ? (
          <RopSection wellUwi={uwi} />
        ) : tab === "npt" ? (
          <NptSection wellUwi={uwi} />
        ) : tab === "timeDepth" ? (
          <TimeDepthSection wellUwi={uwi} />
        ) : tab === "byDepth" ? (
          <ByDepthSection wellUwi={uwi} />
        ) : (
          <EndOfWellSection wellUwi={uwi} />
        )}
      </div>
    </div>
  );
}
